import { FinancialStatementType } from '@interfaces/accounting-data-standardization/responses/statement-detail.response';
import { StandardAccountDictionaryEntry } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary.response';
import {
  StandardAccountDictionaryChangeSource,
  StandardAccountDictionaryProposal,
  StandardAccountDictionaryProposalKind,
  StandardAccountDictionaryProposalRevertResult,
  StandardAccountDictionaryProposalStatus,
} from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';

/** Rótulo do tipo de mudança proposta, como a pessoa lê no cartão. */
const PROPOSAL_KIND_LABELS: Record<StandardAccountDictionaryProposalKind, string> = {
  Include: 'Acrescentar em Entra',
  Exclude: 'Acrescentar em NÃO entra',
  Pitfall: 'Erro comum',
  Definition: 'Nova definição',
};

/** Seção do verbete que a proposta muda (título do bloco "verbete atual"). */
const PROPOSAL_KIND_SECTION: Record<StandardAccountDictionaryProposalKind, string> = {
  Include: 'Entra hoje',
  Exclude: 'NÃO entra hoje',
  Pitfall: 'Erros comuns hoje',
  Definition: 'Definição hoje',
};

const SOURCE_LABELS: Record<StandardAccountDictionaryChangeSource, string> = {
  Seed: 'Carga inicial',
  Manual: 'Edição manual',
  ReviewerProposal: 'Proposta do revisor',
  Rollback: 'Volta de versão',
  AutoReviewerProposal: 'Revisor (automático)',
};

const PROPOSAL_STATUS_LABELS: Record<StandardAccountDictionaryProposalStatus, string> = {
  Pending: 'Pendente',
  Applied: 'Aplicada',
  Rejected: 'Recusada',
  Reverted: 'Desfeita',
};

/** Tag curta do tipo na lista "A revisar" (o destino do "Não entra" vem junto, em `reviewKindLabel`). */
const REVIEW_KIND_LABELS: Record<StandardAccountDictionaryProposalKind, string> = {
  Include: 'Entra',
  Exclude: 'Não entra',
  Pitfall: 'Erro comum',
  Definition: 'Definição',
};

export function proposalKindLabel(kind: StandardAccountDictionaryProposalKind): string {
  return PROPOSAL_KIND_LABELS[kind] ?? kind;
}

/** Tag da linha de revisão: "Entra", "Não entra → destino", "Erro comum" ou "Definição". */
export function reviewKindLabel(proposal: StandardAccountDictionaryProposal): string {
  const label = REVIEW_KIND_LABELS[proposal.kind] ?? proposal.kind;
  const destination = proposal.kind === 'Exclude' ? proposal.goesTo?.trim() : '';
  return destination ? `${label} → ${destination}` : label;
}

export function proposalKindSection(kind: StandardAccountDictionaryProposalKind): string {
  return PROPOSAL_KIND_SECTION[kind] ?? 'Verbete hoje';
}

export function changeSourceLabel(source: StandardAccountDictionaryChangeSource): string {
  return SOURCE_LABELS[source] ?? source;
}

export function proposalStatusLabel(status: StandardAccountDictionaryProposalStatus): string {
  return PROPOSAL_STATUS_LABELS[status] ?? status;
}

export function statementTypeLabel(
  type: FinancialStatementType | string | null | undefined
): string {
  return type === 'Dre' ? 'DRE' : type === 'Balanco' ? 'Balanço' : (type ?? '');
}

/**
 * Itens atuais da seção do verbete que a proposta mexe (texto puro, para comparar com o texto novo). `Definition` devolve
 * a definição como item único; vazio quando o verbete não veio.
 */
export function currentItemsForProposal(proposal: StandardAccountDictionaryProposal): string[] {
  const entry = proposal.currentEntry;
  if (!entry) return [];
  switch (proposal.kind) {
    case 'Include':
      return cleanList(entry.includes);
    case 'Exclude':
      return (entry.excludes ?? [])
        .filter(item => !!item?.item?.trim())
        .map(item => formatExclusion(item.item, item.goesTo));
    case 'Pitfall':
      return cleanList(entry.pitfalls);
    case 'Definition':
      return entry.definition?.trim() ? [entry.definition.trim()] : [];
    default:
      return [];
  }
}

/** Texto novo da proposta como vai aparecer no verbete ("item → destino" na exclusão). */
export function proposedItemText(proposal: StandardAccountDictionaryProposal): string {
  return proposal.kind === 'Exclude'
    ? formatExclusion(proposal.text, proposal.goesTo)
    : (proposal.text ?? '').trim();
}

export function formatExclusion(item: string, goesTo: string | null | undefined): string {
  const destination = goesTo?.trim();
  return destination ? `${item.trim()} → ${destination}` : item.trim();
}

/** Propostas aplicadas sozinhas a partir de um mesmo documento (uma versão por documento, task 3452). */
export interface DictionaryReviewGroup {
  key: string;
  companyId: string | null;
  companyName: string | null;
  extractionId: string | null;
  versionNumber: number | null;
  /** Quando entraram (decisão automática ou, sem ela, quando o revisor propôs). */
  appliedAt: string | null;
  proposals: StandardAccountDictionaryProposal[];
}

/**
 * Agrupa as propostas "a revisar" pelo documento de origem e pela versão em que entraram, da versão mais nova para a
 * mais antiga. Sem documento (de outro tenant), cada versão vira um grupo próprio.
 */
export function groupProposalsForReview(
  proposals: readonly StandardAccountDictionaryProposal[]
): DictionaryReviewGroup[] {
  const groups = new Map<string, DictionaryReviewGroup>();
  for (const proposal of proposals) {
    const key = `${proposal.revenueStatementExtractionId ?? 'sem-documento'}|${proposal.appliedVersionNumber ?? 'sem-versao'}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        companyId: proposal.companyId,
        companyName: proposal.companyName,
        extractionId: proposal.revenueStatementExtractionId,
        versionNumber: proposal.appliedVersionNumber,
        appliedAt: proposal.decidedAt ?? proposal.createdAt,
        proposals: [],
      };
      groups.set(key, group);
    }
    group.proposals.push(proposal);
  }
  return [...groups.values()].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0));
}

/**
 * Número da versão criada pelo "Desfazer". O backend devolve a versão nova, a proposta (quando o item já tinha saído
 * do verbete) ou o envelope `{ proposal, version }`; nulo quando não houve versão nova.
 */
export function revertedVersionNumber(
  result: StandardAccountDictionaryProposalRevertResult | null
): number | null {
  if (!result) return null;
  if ('version' in result) return result.version?.number ?? null;
  if ('kind' in result) return null;
  return typeof result.number === 'number' ? result.number : null;
}

/** Itens da seção do verbete atual com a marca do que é igual ao texto da proposta (para destacar). */
export function currentItemsWithHighlight(
  proposal: StandardAccountDictionaryProposal
): { text: string; isProposed: boolean }[] {
  const proposed = normalizeText(proposedItemText(proposal));
  return currentItemsForProposal(proposal).map(text => ({
    text,
    isProposed: !!proposed && normalizeText(text) === proposed,
  }));
}

/** Grupo de verbetes de um mesmo caminho no plano padrão (pai do verbete). */
export interface DictionaryEntryGroup {
  key: string;
  title: string;
  statementType: FinancialStatementType;
  entries: StandardAccountDictionaryEntry[];
}

/**
 * Agrupa os verbetes pelo caminho do pai (o `path` sem o último nível), na ordem em que vêm do dicionário. Sem `path`,
 * cai no grupo do demonstrativo.
 */
export function groupEntriesByPath(
  entries: readonly StandardAccountDictionaryEntry[]
): DictionaryEntryGroup[] {
  const groups = new Map<string, DictionaryEntryGroup>();
  for (const entry of entries) {
    const parent = parentPath(entry);
    const title = parent || statementTypeLabel(entry.statementType);
    const key = `${entry.statementType}|${title}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, title, statementType: entry.statementType, entries: [] };
      groups.set(key, group);
    }
    group.entries.push(entry);
  }
  return [...groups.values()];
}

/** Filtra por demonstrativo e por texto (rótulo, caminho, definição, entra, não entra, erros comuns, decisões). */
export function filterEntries(
  entries: readonly StandardAccountDictionaryEntry[],
  type: FinancialStatementType | null,
  query: string
): StandardAccountDictionaryEntry[] {
  const needle = normalizeText(query);
  return entries.filter(entry => {
    if (type && entry.statementType !== type) return false;
    if (!needle) return true;
    const haystack = [
      entry.label,
      entry.path,
      entry.definition,
      ...(entry.includes ?? []),
      ...(entry.excludes ?? []).map(item => `${item.item} ${item.goesTo ?? ''}`),
      ...(entry.pitfalls ?? []),
      ...(entry.decisions ?? []),
    ]
      .filter(Boolean)
      .join(' ');
    return normalizeText(haystack).includes(needle);
  });
}

/** Chave estável do verbete (o mesmo `standardAccountId` pode faltar em verbete antigo). */
export function entryKey(entry: StandardAccountDictionaryEntry): string {
  return entry.standardAccountId ?? `${entry.statementType}|${entry.csvId ?? entry.label}`;
}

/** Lista de texto livre (um item por linha) para a lista do verbete: corta espaços e descarta linha vazia. */
export function linesToList(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);
}

export function listToLines(items: readonly string[] | null | undefined): string {
  return cleanList(items).join('\n');
}

function cleanList(items: readonly string[] | null | undefined): string[] {
  return (items ?? []).map(item => item?.trim()).filter((item): item is string => !!item);
}

function parentPath(entry: StandardAccountDictionaryEntry): string {
  const parts = (entry.path ?? '')
    .split('/')
    .map(part => part.trim())
    .filter(part => part.length > 0);
  return parts.length > 1 ? parts.slice(0, -1).join(' / ') : '';
}

function normalizeText(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}
